import {Injectable} from '@angular/core';
import {BookingState} from '../types/booking-state';
import {QueryParams} from '../../../types/QueryParams';
import {BookingV2StateService} from './booking-v2-state.service';
import {RoutesQueryParamsEnum} from '../../../enums/RoutesEnums';
import * as dayjs from 'dayjs';
import 'dayjs/locale/sv';
import * as isoWeek from 'dayjs/plugin/isoWeek';
import {ToolsService} from '../../../services/tools.service';
import {BookingStepsEnum} from '../enums/booking-enums';
import {UrlBuilderService} from '../../../services/url-builder.service';

@Injectable({
  providedIn: 'any',
})
export class BookingV2UrlBuilderService {
  constructor(
    private bookingState: BookingV2StateService,
    private tools: ToolsService,
    private urlBuilder: UrlBuilderService,
  ) {
    dayjs.locale('sv');
    dayjs.extend(isoWeek);
  }

  public getBookingQueryParamsFromState(state: BookingState): QueryParams {
    let queryParams: QueryParams = {};
    const defaultState: BookingState = this.bookingState.getDefaultState();

    if (
      !this.tools.isNil(state.selectedBookingServiceId) &&
      state.selectedBookingServiceId !== defaultState.selectedBookingServiceId
    ) {
      queryParams[RoutesQueryParamsEnum.BOOKING_SELECTED_SERVICE] = state.selectedBookingServiceId;
    }
    if (
      !this.tools.isNil(state.selectedStaffMemberId) &&
      state.selectedStaffMemberId !== defaultState.selectedStaffMemberId
    ) {
      queryParams[RoutesQueryParamsEnum.BOOKING_SELECTED_STAFFMEMBER] = state.selectedStaffMemberId;
    }
    if (state.alert !== defaultState.alert) {
      queryParams[RoutesQueryParamsEnum.BOOKING_ALERT] = state.alert;
    }
    if (state.numberOfTimeslots !== defaultState.numberOfTimeslots) {
      queryParams[RoutesQueryParamsEnum.BOOKING_NUMBER_OF_TIMESLOTS] = state.numberOfTimeslots;
    }
    if (state.selectedDateTime !== defaultState.selectedDateTime) {
      queryParams[RoutesQueryParamsEnum.BOOKING_SELECTED_DATETIME] = state.selectedDateTime;
    }
    if (
      state.calendarWeekStartDate !== defaultState.calendarWeekStartDate ||
      (!this.tools.isNil(state.calendarWeekStartDate) &&
        state.calendarWeekStartDate !== '' &&
        state.forceCalendarDate === true)
    ) {
      queryParams[RoutesQueryParamsEnum.BOOKING_CALENDAR_DATE] = dayjs(state.calendarWeekStartDate).format('YYYYMMDD');
    }
    if (state.referral !== defaultState.referral) {
      queryParams[RoutesQueryParamsEnum.BOOKING_REFERRAL] = encodeURI(state.referral);
    }
    if (state.forceCalendarDate === true) {
      queryParams[RoutesQueryParamsEnum.BOOKING_FORCE_CALENDAR_DATE] = 1;
    }
    if (state.serviceGroup !== defaultState.serviceGroup) {
      queryParams[RoutesQueryParamsEnum.BOOKING_SERVICE_GROUP_ID] = state.serviceGroup;
    }
    if (state.TimeslotMatchExisted !== null) {
      queryParams[RoutesQueryParamsEnum.BOOKING_TIMESLOT_MATCH] = state.TimeslotMatchExisted === true ? 1 : 0;
    }
    if (state.twoFactorAuthenticationRequired === true) {
      queryParams[RoutesQueryParamsEnum.BOOKING_TWO_FACTOR_AUTH] = 1;
    }
    queryParams[RoutesQueryParamsEnum.BOOKING_STEP] = state.step;
    queryParams = this.urlBuilder.cleanQueryParams(queryParams);
    queryParams = this.urlBuilder.sortQueryParams(queryParams);
    return queryParams;
  }

  public getBaseStateForStep(step: number): BookingState {
    switch (step) {
      case BookingStepsEnum.SERVICES:
        return this.getBaseStateForStepServices();
      case BookingStepsEnum.CALENDAR:
        return this.getBaseStateForStepCalendar();
      case BookingStepsEnum.SUBMIT:
        return this.getBaseStateForStepSubmit();
      case BookingStepsEnum.CONFIRM:
        return this.getBaseStateForStepConfirm();
      default:
        return this.getBaseStateForStepServices();
    }
  }

  public getBaseStateForStepServices(): BookingState {
    const newState: BookingState = this.tools.clone(this.bookingState.state$.getValue());
    const defaultState: BookingState = this.tools.clone(this.bookingState.getDefaultState());
    newState.step = BookingStepsEnum.SERVICES;
    newState.selectedStaffMemberId = defaultState.selectedStaffMemberId;
    newState.selectedBookingServiceId = defaultState.selectedBookingServiceId;
    newState.selectedDateTime = defaultState.selectedDateTime;
    newState.selectedStaffMemberId = defaultState.selectedStaffMemberId;
    newState.selectedStaffMemberId = defaultState.selectedStaffMemberId;
    newState.calendarWeekStartDate = defaultState.calendarWeekStartDate;
    newState.calendarWeekEndDate = defaultState.calendarWeekEndDate;
    newState.alert = defaultState.alert;
    // Ignore selected service and reload whole list when back to the services list
    // https://varden.atlassian.net/browse/VS-3434
    newState.timeslot = defaultState.timeslot;
    newState.serviceId = defaultState.serviceId;
    newState.forceCalendarDate = defaultState.forceCalendarDate;
    newState.serviceGroup = defaultState.serviceGroup;
    newState.TimeslotMatchExisted = defaultState.TimeslotMatchExisted;
    newState.twoFactorAuthenticationRequired = defaultState.twoFactorAuthenticationRequired;
    return newState;
  }

  public getBaseStateForStepCalendar(): BookingState {
    const newState: BookingState = this.tools.clone(this.bookingState.state$.getValue());
    const defaultState: BookingState = this.tools.clone(this.bookingState.getDefaultState());
    newState.selectedDateTime = defaultState.selectedDateTime;
    newState.TimeslotMatchExisted = defaultState.TimeslotMatchExisted;
    newState.step = BookingStepsEnum.CALENDAR;
    return newState;
  }

  public getBaseStateForStepSubmit(): BookingState {
    const newState: BookingState = this.tools.clone(this.bookingState.state$.getValue());
    const defaultState: BookingState = this.tools.clone(this.bookingState.getDefaultState());
    newState.TimeslotMatchExisted = defaultState.TimeslotMatchExisted;
    newState.step = BookingStepsEnum.SUBMIT;
    return newState;
  }

  public getBaseStateForStepConfirm(): BookingState {
    const newState: BookingState = this.tools.clone(this.bookingState.state$.getValue());
    const defaultState: BookingState = this.tools.clone(this.bookingState.getDefaultState());
    newState.TimeslotMatchExisted = defaultState.TimeslotMatchExisted;
    newState.step = BookingStepsEnum.CONFIRM;
    return newState;
  }
}
